// FitToViewport — hybrid exercise page-fit (scale-on-tablet, scroll-on-phone).
//
// The fit logic lives in the useFitToViewport HOOK so the session shell in
// App.jsx can render PLAIN <div>s (the same element type as the non-session
// shell). That matters: when the route changes from a session route to a
// non-session route (e.g. /play/session -> /play/math-coach), App swaps which
// branch it renders. If the session branch were a distinct *component*
// (<FitToViewport>) and the non-session branch a host <div>, React reconciles
// across different element types and — combined with this component's layout
// effects — the route content could fail to swap (the coach screen never
// mounted; the exercise stayed frozen on screen). Rendering both branches as
// <div> makes it a div->div reconciliation so the <Outlet/> always updates.
//
//   • >=md (tablet/desktop): measure content vs available height and apply
//     transform: scale(s) (s = min(1, avail/content), never upscaling) from the
//     top center, with the outer overflow-hidden — one page, no scroll, nothing
//     clipped. A transform doesn't change the layout box so scrollHeight is read
//     untransformed (no measure feedback loop).
//   • <md (phone): no transform; outer is overflow-y-auto so controls never clip.
//   • MIN_SCALE floor: rather than shrink below legibility, fall back to scroll.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

// Tablet/fit path = Tailwind `md` (>=768px) OR a COARSE-POINTER device at
// >=700px. C3 (Batch 1.5): the primary-target iPad Mini portrait is 744px —
// below 768 it silently dropped to the iPhone scroll path. The coarse-pointer
// clause catches touch tablets in the 700–767px band without pulling in a
// narrow desktop window (fine pointer). The MIN_SCALE floor below keeps tap
// targets legible at the resulting scale.
const MD_QUERY = "(min-width: 768px), (min-width: 700px) and (pointer: coarse)";

// LEGIBILITY FLOOR — for a 4–7yo, shrinking below this hurts readability/tap
// targets more than a little scrolling; below it we scroll instead of scaling.
export const MIN_SCALE = 0.6;

/** Pure scale math (unit-tested). Factor to fit `availableHeight`, never
 *  upscaling (cap 1). Returns 1 for degenerate inputs so we never emit a
 *  0 / NaN / Infinity transform. */
export function computeFitScale(
  availableHeight: number,
  contentHeight: number,
): number {
  if (
    !Number.isFinite(availableHeight) ||
    !Number.isFinite(contentHeight) ||
    contentHeight <= 0
  ) {
    return 1;
  }
  return Math.min(1, availableHeight / contentHeight);
}

/** Mode decision with the MIN_SCALE floor (pure, unit-tested). */
export function fitMode(
  availableHeight: number,
  contentHeight: number,
  minScale: number,
): { mode: "scale" | "scroll"; scale: number } {
  const fit = computeFitScale(availableHeight, contentHeight);
  if (fit < minScale) {
    return { mode: "scroll", scale: 1 };
  }
  return { mode: "scale", scale: fit };
}

// MA-622 R1 — the pad's clamp: never below a 140px legibility floor, never
// above its own natural (unconstrained) height; in between, take exactly the
// space `releasedPx` frees up minus the bonus block's own fixed chrome
// (prompt line + button row). Pure, unit-tested (fitScale.test.mjs).
export const PAD_MIN_HEIGHT_PX = 140;

// RED finding 2 — residual frozen-scale overflow at or below this many px is
// tolerated as overflow-hidden (no scroll-container flip); see frozenOverflow
// below for why.
export const OVERFLOW_TOLERANCE_PX = 24;

export function padHeightFor(
  releasedPx: number,
  chromePx: number,
  naturalPx: number,
): number {
  const nat = Number.isFinite(naturalPx) && naturalPx > 0 ? naturalPx : PAD_MIN_HEIGHT_PX;
  // RED finding 5 — a naturally SHORT pad (a narrow phone viewport's pad,
  // fixed aspect ratio over a narrow width) must never be INFLATED past its
  // own natural size just to reach the 140px floor — the floor is a "don't
  // shrink below this" rule, not a "grow up to this" one. Only when natural
  // is already >= the floor does the floor/ceiling clamp below apply.
  if (nat < PAD_MIN_HEIGHT_PX) return nat;
  const rel = Number.isFinite(releasedPx) ? releasedPx : 0;
  const chr = Number.isFinite(chromePx) ? chromePx : 0;
  const target = rel - chr;
  return Math.min(nat, Math.max(PAD_MIN_HEIGHT_PX, target));
}

/** Pure (unit-tested): the trace-bonus block's own non-canvas chrome, in px —
 *  everything in the block that ISN'T the pad's canvas box (prompt line,
 *  Skip/Next row) PLUS the block's own top margin (mt-5/md:mt-6), which sits
 *  OUTSIDE `blockOffsetHeight` (offsetHeight never includes an element's own
 *  margin) but still eats into the space padHeightFor budgets against.
 *  ARBITER round-5 item 2 (pin) — the margin term used to be easy to drop
 *  silently (zeroing it doesn't fail any existing assertion for Math, whose
 *  residual has headroom); extracting this as a named, unit-tested pure
 *  function makes an accidental drop of the margin term a one-line unit-test
 *  regression instead of a silent regression. */
export function padChromePx(
  blockOffsetHeight: number,
  canvasOffsetHeight: number,
  blockMarginTop: number,
): number {
  const block = Number.isFinite(blockOffsetHeight) ? blockOffsetHeight : 0;
  const canvas = Number.isFinite(canvasOffsetHeight) ? canvasOffsetHeight : 0;
  const margin = Number.isFinite(blockMarginTop) ? blockMarginTop : 0;
  return Math.max(0, block - canvas + margin);
}

// RED round-4 (design fix, not a loosening) — the post-answer trace-bonus
// block's WORST-CASE total footprint: the 140px canvas floor + its own
// non-canvas chrome (prompt line + Skip/Next row, ~132px measured across
// Math/Mixed/Reading) + its own top margin (24px, mt-5/md:mt-6) = 296px.
// This is a CONSTANT, not computed per-item, because it has to match at
// MOUNT time, before the bonus block exists to measure. Math's collapsible
// tiles+hint block already releases >=296px for ordinary items so it never
// needed this; Reading's (compact 2-choice word tiles, smaller hint margin)
// can fall ~30-40px short — the residual overflow RED round 3 found, which
// isn't a "wasted space" bug (round 3's footer padding fix, while itself a
// real, worth-keeping defect, doesn't touch this) but a genuine shortfall
// this constant + padReservePx below closes by reserving room UP FRONT.
//
// e2e/62's overflow-hidden assertions for the reading no-fallback cases
// (1024x720, 1366x768) are effectively PINNING this constant — if the
// bonus block's real chrome/margin ever grows, this must grow with it or
// those assertions go red again (a deliberate tripwire, not flakiness).
export const PAD_BONUS_FOOTPRINT_PX = 296;

/** Pure (unit-tested): how many px of invisible spacer to reserve, at item
 *  mount, after the collapsible tiles+hint block, so the frozen scale
 *  already budgets room for the post-answer bonus block's full footprint —
 *  0 when the block being released is already >= the footprint (Math's
 *  ordinary case; nothing to reserve, no visual change). */
export function padReservePx(footprintPx: number, releasedPx: number): number {
  const footprint = Number.isFinite(footprintPx) && footprintPx > 0 ? footprintPx : 0;
  const releasedRaw = Number.isFinite(releasedPx) ? releasedPx : 0;
  const released = Math.max(0, releasedRaw);
  return Math.max(0, footprint - released);
}

// MA-622 R1 — freezeKey plumbing. useFitToViewport is called once, in
// App.jsx, above the <Outlet/> — it has no cheap way to know which item a
// session screen (ReadingSession/MathSession/MixedSession) is currently
// serving, that state lives several components down. Rather than thread the
// item id back up as a prop (coupling App.jsx to every session screen's
// shape), the hook exposes a tiny setter via context: App.jsx provides
// `fitViewport.setFreezeKey` on FitFreezeKeyContext around the session
// Outlet, and a session screen calls the useFitFreezeKey(itemId) convenience
// hook below with its own current item id. Minimal — one function passed
// down, one effect calling it.
export const FitFreezeKeyContext = createContext<(key: string | null) => void>(
  () => {},
);

/** Pure freeze decision (unit-tested): may useFitToViewport's measure() pass
 *  actually change `scale`/`floorScroll`, or must it only update
 *  `contentOverflowPx` and leave the applied scale alone? True on the very
 *  first measurement (`hasAppliedBefore` false), a forced call (window
 *  resize/orientationchange), or a freezeKey change (a new item/screen) —
 *  the three triggers R1 names. False otherwise, e.g. content growing (the
 *  trace pad mounting, a hint scaffold expanding) while the key is
 *  unchanged. */
export function shouldRescale(
  prevKey: string | null,
  nextKey: string | null,
  forceRescale: boolean,
  hasAppliedBefore: boolean,
): boolean {
  if (!hasAppliedBefore) return true;
  if (forceRescale) return true;
  return prevKey !== nextKey;
}

/** MA-823 — the freeze key a session screen reports for its served item:
 *  the item id. MA-622 folded the hint's open state into the key
 *  (`${id}|h`), so opening or closing a hint was a key change and rescaled
 *  the whole page inside an item (founder sighting 2026-10-04, ME_11_040 at
 *  1180x820: 0.631 -> scroll fallback). The founder's invariant is that the
 *  visible scale never changes inside an item, so nothing the CHILD does
 *  within an item — a hint opening or closing, a hint tab switch, a withheld
 *  picture coming back, a correct answer and the pad mounting — is part of
 *  the key. Content that grows past the frozen scale is reached through the
 *  existing frozen-overflow scroll (`frozenOverflow` below), never a
 *  rescale. All four session screens call
 *  `useFitFreezeKey(fitFreezeKeyForItem(item?.id, ...))`.
 *
 *  `pending` (MA-823 RED S4, operator ruling): the screen's item-start page
 *  still depends on state that is loading (MathSession: the learner's skill
 *  states, which decide whether the picture is shown, staged or withheld).
 *  While pending the key carries a `|pending` suffix; when the state
 *  resolves the key drops it, which is a key change, so the page is fitted
 *  once more to its real item-start content. A screen must hold `pending`
 *  true for the rest of the item once the child has acted while it was
 *  pending (a tap, a hint), so a late resolution never rescales after an
 *  interaction. */
export const FIT_KEY_PENDING_SUFFIX = "|pending";
export function fitFreezeKeyForItem(
  itemId: string | null | undefined,
  opts?: { pending?: boolean },
): string | null {
  if (typeof itemId !== "string" || itemId.length === 0) return null;
  return opts?.pending ? `${itemId}${FIT_KEY_PENDING_SUFFIX}` : itemId;
}

/** MA-823 RED S4 — pure: should the fit key stay pending? True while the
 *  item-start state is still loading, and — once the child has acted while
 *  it was loading — for the rest of the item (`lockedWhilePending`, which
 *  the screen latches per item from `shouldLockPendingFit`). */
export function fitKeyPending(stateLoading: boolean, lockedWhilePending: boolean): boolean {
  return stateLoading || lockedWhilePending;
}

/** MA-823 RED S4 — pure: latch the pending key for the rest of the item when
 *  the child acts while the item-start state is still loading. */
export function shouldLockPendingFit(stateLoading: boolean, childActed: boolean): boolean {
  return stateLoading && childActed;
}

/** MA-823 — pure (unit-tested): how far (visual px, + = down) to scroll the
 *  page's scroll container so the hint + answer block is in view, given
 *  viewport-relative rects. Like `scrollIntoView({ block: "nearest" })`: 0
 *  when the block is already fully on screen; its bottom edge to the
 *  viewport bottom when it fits; its top edge to the viewport top when it is
 *  taller than the viewport. RED S4 (operator ruling, "no top-clip"): when a
 *  `keepTop` rect is given (the question's stem — the picture, else the
 *  prompt), the result never puts its top above the viewport top; the stem
 *  wins over the block's bottom when both cannot fit, and the rest stays
 *  reachable by the page's scroll. */
export function hintRevealScrollDelta(
  view: { top: number; bottom: number },
  block: { top: number; bottom: number },
  keepTop?: { top: number } | null,
): number {
  const viewH = view.bottom - view.top;
  const blockH = block.bottom - block.top;
  let delta = 0;
  if (blockH > viewH) delta = block.top - view.top;
  else if (block.bottom > view.bottom) delta = block.bottom - view.bottom;
  else if (block.top < view.top) delta = block.top - view.top;
  if (keepTop && Number.isFinite(keepTop.top)) delta = Math.min(delta, keepTop.top - view.top);
  return Number.isFinite(delta) ? delta : 0;
}

function scrollContainerOf(el: HTMLElement): HTMLElement | null {
  for (let p = el.parentElement; p; p = p.parentElement) {
    const oy = getComputedStyle(p).overflowY;
    if ((oy === "auto" || oy === "scroll" || oy === "hidden") && p.scrollHeight > p.clientHeight) return p;
  }
  return null;
}

/** MA-823 — the other half of "the scale never changes inside an item": a
 *  hint that opens ABOVE the answer area (Math/Mixed/Reading render the hint
 *  first inside their tiles+hint block; Foundation's answer area follows the
 *  hint) used to make room by shrinking the page; with the scale frozen it
 *  pushes the answers down instead, past the fold when the page no longer
 *  fits. While the hint is open, `observedRef`'s element is watched with a
 *  ResizeObserver: on its first layout (the panel just opened) and whenever
 *  its size changes (RED S3: a hint tab switch to a taller rung, a late
 *  image), `targetOf(element)` (default: the element itself) is brought into
 *  view by the minimal scroll of the page's own scroll container
 *  (`hintRevealScrollDelta`), never past the top of `keepTop()` (RED S4). It
 *  never runs while the hint is closed, and never on a scroll alone. */
export function useRevealHintBlock(
  open: boolean,
  observedRef: { readonly current: HTMLElement | null },
  opts?: {
    targetOf?: (observed: HTMLElement) => HTMLElement | null;
    keepTop?: () => HTMLElement | null;
  },
): void {
  const optsRef = useRef(opts);
  optsRef.current = opts;
  useEffect(() => {
    if (!open) return;
    const observed = observedRef.current;
    if (!observed) return;
    const reveal = () => {
      const o = optsRef.current;
      const target = o?.targetOf ? o.targetOf(observed) : observed;
      if (!target || !target.isConnected) return;
      const scroller = scrollContainerOf(target);
      if (!scroller) return;
      const view = scroller.getBoundingClientRect();
      const keep = o?.keepTop?.() ?? null;
      const delta = hintRevealScrollDelta(view, target.getBoundingClientRect(), keep && keep.isConnected ? keep.getBoundingClientRect() : null);
      if (Math.abs(delta) >= 1) scroller.scrollTop += delta;
    };
    if (typeof ResizeObserver === "undefined") {
      if (typeof requestAnimationFrame !== "function") return;
      const id = requestAnimationFrame(reveal);
      return () => cancelAnimationFrame(id);
    }
    let lastSize = "";
    const ro = new ResizeObserver((entries) => {
      const box = entries[entries.length - 1]?.contentRect;
      const size = box ? `${Math.round(box.width)}x${Math.round(box.height)}` : "";
      if (size === lastSize) return;
      lastSize = size;
      reveal();
    });
    ro.observe(observed);
    return () => ro.disconnect();
    // observedRef is a stable ref object; opts are read through a ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
}

/** MA-823 — the fit hook's applied state, as the pure step below sees it. */
export interface FitFreezeState {
  /** The freeze key the applied scale was computed for. */
  key: string | null;
  scale: number;
  floorScroll: boolean;
  /** False until the first measurement has applied a scale. */
  applied: boolean;
  /** MA-760 — the available height the applied scale is for (the baseline a
   *  height-only change scales proportionally from). Optional so callers
   *  that build a state by hand keep working; absent/0 = no baseline. */
  available?: number;
}

export const INITIAL_FIT_STATE: FitFreezeState = Object.freeze({
  key: null,
  scale: 1,
  floorScroll: false,
  applied: false,
  available: 0,
});

/** MA-823 — one measure() pass as a pure function (unit-tested, and the
 *  exact code useFitToViewport's measure() runs): on a rescale trigger
 *  (`shouldRescale`) the scale and the MIN_SCALE scroll fallback are
 *  recomputed from the current heights; otherwise the applied state is
 *  kept and only the frozen-scale overflow (visual px past the available
 *  height) is reported. */
export function fitStep(
  prev: FitFreezeState,
  input: {
    key: string | null;
    forceRescale: boolean;
    availableHeight: number;
    contentHeight: number;
  },
): { next: FitFreezeState; overflowPx: number } {
  if (shouldRescale(prev.key, input.key, input.forceRescale, prev.applied)) {
    const { mode, scale } = fitMode(input.availableHeight, input.contentHeight, MIN_SCALE);
    return {
      next: { key: input.key, scale, floorScroll: mode === "scroll", applied: true, available: input.availableHeight },
      overflowPx: 0,
    };
  }
  // MA-760 — ONLY the available height changed (the offline banner or the
  // online "Saving…" line appearing/disappearing moves the session frame to
  // `100dvh - bannerPx`): scale the frozen scale by the height ratio, never
  // refit to the content. A refit would size the page to whatever is inside
  // the item right now (an open hint, the mounted pad), which is exactly the
  // content-driven rescale MA-823 removed. Below MIN_SCALE it falls into the
  // scroll fallback, as fitMode does; capped at 1. Window resize and
  // orientation stay full refits (forceRescale above).
  const baseline = prev.available ?? 0;
  if (
    !prev.floorScroll &&
    baseline > 0 &&
    Number.isFinite(input.availableHeight) &&
    input.availableHeight > 0 &&
    Math.abs(input.availableHeight - baseline) >= 1
  ) {
    const scaled = Math.min(1, (prev.scale * input.availableHeight) / baseline);
    const floor = scaled < MIN_SCALE;
    const next: FitFreezeState = {
      key: prev.key,
      scale: floor ? 1 : scaled,
      floorScroll: floor,
      applied: true,
      available: input.availableHeight,
    };
    const virtual = input.contentHeight * next.scale;
    return { next, overflowPx: floor ? 0 : Math.max(0, virtual - input.availableHeight) };
  }
  const virtualHeight = input.contentHeight * prev.scale;
  return { next: prev, overflowPx: Math.max(0, virtualHeight - input.availableHeight) };
}

/** MA-964 (D-1017-13) — the inner's content height in LAYOUT px, refined
 *  below the pixel where it can be. `scrollHeight` is an integer: Chromium rounds the true scrollable
 *  extent, so a fractional layout (MA-912's tap-floor rule grows controls by
 *  44px / scale) can read up to 1 px short, and a settle measured on it lands
 *  that fraction too large and clips the control at the frame's foot
 *  (e2e/181 DTR_trace_see_01's Done: extent 983.25, scrollHeight 983, Done
 *  clipped 0.22 px at 1180x820). `extentPx` is the fractional bottom of the
 *  inner's content (the deepest descendant border box, in layout px). It is
 *  exact only when nothing (trailing padding or margin) sits between that box
 *  and the scroll extent; otherwise ext < sh and the rounded value stands, so
 *  it is never worse than scrollHeight alone (RED r2 N2-1). It only
 *  REFINES the rounded value inside the window rounding can hide,
 *  [scrollHeight, scrollHeight + 1]: never below scrollHeight (a rounded-up
 *  read is kept) and never more than 1 px above it (a box inside a clipped or
 *  transformed ancestor cannot inflate the fit). Pure, so it is unit-tested. */
export function layoutContentHeight(scrollHeight: number, extentPx: number): number {
  const sh = Number.isFinite(scrollHeight) && scrollHeight > 0 ? scrollHeight : 0;
  const ext = Number.isFinite(extentPx) && extentPx > 0 ? extentPx : 0;
  return Math.max(sh, Math.min(ext, sh + 1));
}

/** The DOM read for layoutContentHeight. The inner is transformed by the
 *  scale already applied (its computed matrix, `none` -> 1), so client-rect
 *  offsets from its top are divided back into layout px. Cost: one
 *  getBoundingClientRect per descendant per measure (each settle pass and the
 *  ~750 ms poll below), after the layout the measure already forces; linear
 *  in the session DOM (a few hundred nodes), acceptable there (RED r2 N2-2). */
function measureInnerHeight(inner: HTMLElement): number {
  const sh = inner.scrollHeight;
  const m = getComputedStyle(inner).transform.match(/^matrix\(([^,]+),/);
  const applied = m ? parseFloat(m[1]) || 1 : 1;
  const top = inner.getBoundingClientRect().top;
  let bottom = 0;
  for (const el of inner.querySelectorAll("*")) {
    const b = el.getBoundingClientRect().bottom;
    if (b > bottom) bottom = b;
  }
  return layoutContentHeight(sh, bottom > top ? (bottom - top) / applied : 0);
}

/** MA-912 (RED r1 M-1) — one measure() pass when the content height can
 *  depend on the scale itself. The tap-floor rule (globals.css) grows a
 *  floored control to 44px / s; that growth is layout-neutral except where a
 *  control grows past its own top margin, which is real layout growth. So on
 *  a rescale trigger the scale fitStep picks from the height at the OLD
 *  scale is re-checked with `heightAt(s)` (the content height with the
 *  tap-floor rule evaluated at s): while the page laid out at s does not fit
 *  at s, the scale steps down to the fit of that page (the content height is
 *  non-increasing in s, so the steps go one way and settle in a few passes),
 *  falling into the MIN_SCALE scroll fallback exactly as fitStep does. When
 *  the height does not depend on the scale (every layout-neutral page) the
 *  first check confirms the first pick: identical to fitStep. Non-rescale
 *  passes (frozen, MA-760 proportional) are fitStep unchanged. Pure. */
export function settleFitStep(
  prev: FitFreezeState,
  input: { key: string | null; forceRescale: boolean; availableHeight: number; contentHeight: number },
  heightAt: (scale: number) => number,
  maxPasses = 6,
): { next: FitFreezeState; overflowPx: number } {
  let res = fitStep(prev, input);
  if (!shouldRescale(prev.key, input.key, input.forceRescale, prev.applied)) return res;
  for (let k = 0; k < maxPasses; k++) {
    const s = res.next.scale;
    if (res.next.floorScroll || !(s < 1)) return res;
    const h = heightAt(s);
    if (!Number.isFinite(h) || h <= 0) return res;
    const r = fitStep(prev, { ...input, contentHeight: h });
    if (r.next.floorScroll) return r;
    if (r.next.scale >= s - 1e-6) return res;
    res = r;
  }
  return res;
}

/** Call from a session screen with the currently-served item's id (or null
 *  when there is none yet). Freezes useFitToViewport's scale to that key —
 *  see the hook's freezeKey doc below. */
export function useFitFreezeKey(itemKey: string | null): void {
  const setFreezeKey = useContext(FitFreezeKeyContext);
  useEffect(() => {
    setFreezeKey(itemKey);
  }, [setFreezeKey, itemKey]);
  // RED finding 6 — cleanup on TRUE unmount only (a separate effect with
  // stable deps): a screen that stops calling this hook (route change away
  // from the session) must not leave a stale key wedged in the hook's
  // state forever. Attaching this cleanup to the effect above instead would
  // fire it on every itemKey change too (React tears down + reruns an
  // effect whose deps changed) — a spurious null pulse on every item
  // transition, double-computing the freeze decision for no reason.
  useEffect(() => () => setFreezeKey(null), [setFreezeKey]);
}

function getInitialIsMd(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return true;
  }
  return window.matchMedia(MD_QUERY).matches;
}

interface FitViewport {
  outerRef: React.RefCallback<HTMLDivElement>;
  innerRef: React.RefCallback<HTMLDivElement>;
  outerClassName: string;
  innerStyle: React.CSSProperties;
  /** MA-622 R1: hand this to FitFreezeKeyContext.Provider so a session
   *  screen (via useFitFreezeKey) can report its current item id. */
  setFreezeKey: (key: string | null) => void;
  /** MA-622 R1: how far the (frozen-scale) content currently overflows the
   *  available height, in visual px — 0 when nothing overflows. Updated by
   *  the content observers even while the scale itself is frozen, so a
   *  caller (or this hook's own scroll fallback below) can react. */
  contentOverflowPx: number;
  /** MA-790 (g): the capture probe for the session frame outer — the
   *  effective fit scale and the fit mode as data attributes (see
   *  fitProbeAttrs). Spread onto the same outer <div> as outerClassName. */
  outerDataAttrs: FitProbeAttrs;
}

/** MA-790 (g) — the two capture-probe attributes on the session frame outer. */
export interface FitProbeAttrs {
  /** The effective scale: the applied transform scale, else "1". At most 4 decimals. */
  "data-fit-scale": string;
  /** Closed set: "scale" (tablet/desktop scale path, incl. a frozen scale whose
   *  content overflows), "fallback" (the MIN_SCALE scroll fallback, no
   *  transform), "inactive" (no fit runs: a non-session route or the phone path). */
  "data-fit-mode": "scale" | "fallback" | "inactive";
}

/** MA-790 (g) — pure: the capture-probe attributes for a fit state. Reads the
 *  SAME inputs as useFitToViewport's `innerStyle` and `fitModeClass`, so the
 *  attribute can never disagree with the transform the page renders. Numbers
 *  and a closed set only (answer-safe). */
export function fitProbeAttrs(s: { active: boolean; isMd: boolean; floorScroll: boolean; scale: number }): FitProbeAttrs {
  const on = s.active && s.isMd;
  const applied = on && !s.floorScroll && s.scale !== 1 && Number.isFinite(s.scale);
  const scale = applied ? Math.round(s.scale * 10000) / 10000 : 1;
  return {
    "data-fit-scale": String(scale),
    "data-fit-mode": !on ? "inactive" : s.floorScroll ? "fallback" : "scale",
  };
}

/** MA-912 — pure: the inner element's style for a fit state. The scale path
 *  carries the transform AND the `--fit-scale` custom property, which the
 *  tap-floor rule in globals.css (`[data-fit-mode="scale"] ...`) reads so a
 *  control with a tap-target floor (min-h-[44px], min-h-[48px], ...) keeps a
 *  RENDERED box of at least 44x44 px after the scale: its min-height and
 *  min-width become max(floor, 44px / scale), and a negative block margin of
 *  the same growth keeps its layout (margin-box) height, so the measured
 *  content height, and with it the fit scale, does not move. Every other
 *  path (the phone path, the MIN_SCALE scroll fallback, a non-session route,
 *  a scale of exactly 1) returns `{}`, byte-identical to before MA-912. */
export function fitInnerStyle(s: { active: boolean; isMd: boolean; floorScroll: boolean; scale: number }): React.CSSProperties {
  const applyScale = s.active && s.isMd && !s.floorScroll && s.scale !== 1 && Number.isFinite(s.scale) && s.scale > 0;
  if (!applyScale) return {};
  return {
    transform: `scale(${s.scale})`,
    transformOrigin: "top center",
    ["--fit-scale" as string]: String(s.scale),
  } as React.CSSProperties;
}

/**
 * Hook form of the page-fit shell. Call it UNCONDITIONALLY at the top of the
 * component (rules of hooks); pass `active=false` on non-session routes so it
 * stops measuring and emits no transform. Spread the returned refs/className/
 * style onto a plain outer+inner <div> pair wrapping the route content.
 */
export function useFitToViewport(active: boolean): FitViewport {
  const outerNodeRef = useRef<HTMLDivElement | null>(null);
  const innerNodeRef = useRef<HTMLDivElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const roRef = useRef<ResizeObserver | null>(null);
  const moRef = useRef<MutationObserver | null>(null);

  const [isMd, setIsMd] = useState<boolean>(getInitialIsMd);
  const [scale, setScale] = useState<number>(1);
  const [floorScroll, setFloorScroll] = useState<boolean>(false);

  // MA-622 R1 — freeze. `freezeKey` (set via setFreezeKey / useFitFreezeKey,
  // typically the session's current item id) gates whether measure() below
  // is allowed to change `scale`/`floorScroll`. scaleRef mirrors `scale` so
  // measure's rAF callback can read the LATEST applied scale without adding
  // it as a measure dependency (which would tear down/reattach the content
  // observers on every scale change).
  const [freezeKey, setFreezeKey] = useState<string | null>(null);
  const [contentOverflowPx, setContentOverflowPx] = useState<number>(0);
  const freezeKeyRef = useRef<string | null>(null);
  const scaleRef = useRef<number>(1);
  const floorScrollRef = useRef(false);
  const hasAppliedRef = useRef(false);
  // MA-760 — the available height the applied scale is for (fitStep's
  // proportional baseline).
  const appliedAvailableRef = useRef(0);
  const pendingForceRescaleRef = useRef(false);
  // RED finding 6/7 — measure() reads the REQUESTED freeze key through this
  // ref rather than closing over the `freezeKey` state value directly. That
  // keeps `freezeKey` out of measure's dependency array, so measure (and the
  // attachObservers/outerRef/innerRef callbacks built on it below) keep a
  // STABLE identity across item transitions instead of being torn down and
  // recreated on every single item — which would otherwise tear down and
  // reattach the ResizeObserver/MutationObserver on every item too. The
  // layout effect below (keyed on freezeKey) syncs the ref before calling
  // measure(), so measure always sees the current key at call time.
  const requestedFreezeKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return;
    }
    const mql = window.matchMedia(MD_QUERY);
    const onChange = () => setIsMd(mql.matches);
    setIsMd(mql.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  const measure = useCallback((opts?: { forceRescale?: boolean }) => {
    // Coalesce rapid calls into ONE trailing rAF. We must NOT
    // cancel-and-reschedule: under continuous DOM churn (the correct-tile
    // sparkle particles, the pal speech-bubble, etc. mutate the subtree every
    // frame) a cancel-each-time debounce would never actually fire, starving
    // the re-measure and leaving the fit scale stale right when the handwriting
    // pad mounts. With a coalescing guard the first call wins the frame and
    // runs; later calls simply re-arm the next frame after it completes.
    //
    // MA-622 R1: forceRescale accumulates across coalesced calls (a resize
    // arriving in the same frame as a content mutation must not lose its
    // "rescale anyway" intent to whichever call happened to arrive first).
    if (opts?.forceRescale) pendingForceRescaleRef.current = true;
    if (rafRef.current !== null) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      const forceRescale = pendingForceRescaleRef.current;
      pendingForceRescaleRef.current = false;
      if (!active || !isMd) {
        scaleRef.current = 1;
        floorScrollRef.current = false;
        setScale((prev) => (prev === 1 ? prev : 1));
        setFloorScroll((prev) => (prev === false ? prev : false));
        setContentOverflowPx((prev) => (prev === 0 ? prev : 0));
        return;
      }
      const outer = outerNodeRef.current;
      const inner = innerNodeRef.current;
      if (!outer || !inner) return;
      const availableHeight = outer.clientHeight;
      const contentHeight = measureInnerHeight(inner); // layout px, sub-pixel exact (MA-964)

      // MA-622 R1 — freeze: rescale only on a freezeKey change, a forced
      // (window resize/orientationchange) call, or the very first measure
      // (hasAppliedRef guards the initial mount, where freezeKeyRef and
      // freezeKey both start `null` and would otherwise look "unchanged").
      // Any OTHER trigger (ResizeObserver/MutationObserver content growth —
      // e.g. the trace pad mounting) with the key unchanged only updates
      // contentOverflowPx; the scale itself does not move.
      // MA-823: the decision is the pure fitStep (unit-tested); the refs
      // below are its state.
      const prevState: FitFreezeState = {
        key: freezeKeyRef.current,
        scale: scaleRef.current,
        floorScroll: floorScrollRef.current,
        applied: hasAppliedRef.current,
        available: appliedAvailableRef.current,
      };
      // MA-912 (RED r1 M-1): settle the scale against the page laid out at
      // that scale (the tap-floor rule reads --fit-scale), then leave the
      // custom property at the settled value (React's innerStyle writes the
      // same value; it is removed when no scale applies).
      const heightAt = (s: number) => {
        inner.style.setProperty("--fit-scale", String(s));
        return measureInnerHeight(inner);
      };
      const appliedVar = inner.style.getPropertyValue("--fit-scale");
      const { next: nextState, overflowPx: overflow } = settleFitStep(prevState, {
        key: requestedFreezeKeyRef.current,
        forceRescale,
        availableHeight,
        contentHeight,
      }, heightAt);
      if (nextState !== prevState) {
        if (!nextState.floorScroll && nextState.scale !== 1) inner.style.setProperty("--fit-scale", String(nextState.scale));
        else inner.style.removeProperty("--fit-scale");
      } else if (inner.style.getPropertyValue("--fit-scale") !== appliedVar) {
        if (appliedVar) inner.style.setProperty("--fit-scale", appliedVar);
        else inner.style.removeProperty("--fit-scale");
      }
      if (nextState !== prevState) {
        appliedAvailableRef.current = nextState.available ?? availableHeight;
        hasAppliedRef.current = nextState.applied;
        freezeKeyRef.current = nextState.key;
        const next = nextState.scale;
        const nextFloorScroll = nextState.floorScroll;
        scaleRef.current = next;
        floorScrollRef.current = nextFloorScroll;
        setScale((prev) => (prev === next ? prev : next));
        setFloorScroll((prev) => (prev === nextFloorScroll ? prev : nextFloorScroll));
      }
      setContentOverflowPx((prev) => (prev === overflow ? prev : overflow));
    });
  }, [active, isMd]);

  // MA-622 R1 — a freezeKey change (new item) is one of the three allowed
  // rescale triggers even when no observer happens to fire on its own.
  // RED finding 6/7: sync the ref BEFORE calling measure() so measure's rAF
  // callback (which may run on a later frame) reads the up-to-date key.
  useLayoutEffect(() => {
    requestedFreezeKeyRef.current = freezeKey;
    measure();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [freezeKey]);

  // (Re)attach the content observers to the CURRENT nodes. This lives in the
  // callback refs below — NOT an effect — on purpose: the session's inner <div>
  // mounts on a LATER commit than when this hook's effects first run (the gate
  // ladder renders <Loading/> first), so an effect reading the ref would capture
  // a NULL node and never attach. That was the real bug: with no observer ever
  // attached, the fit only re-measured on window resize, so the handwriting pad
  // mounting mid-session left the scale stale and the pad got clipped (>=md,
  // overflow-hidden) with no way to scroll to it. A callback ref runs exactly
  // when the node attaches/detaches, so the observers are always live.
  const attachObservers = useCallback(() => {
    roRef.current?.disconnect();
    roRef.current = null;
    moRef.current?.disconnect();
    moRef.current = null;
    const inner = innerNodeRef.current;
    const outer = outerNodeRef.current;
    if (typeof ResizeObserver !== "undefined" && inner) {
      const ro = new ResizeObserver(() => measure());
      ro.observe(inner);
      // Watch the OUTER too: a 100dvh change (mobile URL bar showing/hiding)
      // resizes the outer but NOT the flex-pinned inner.
      if (outer) ro.observe(outer);
      roRef.current = ro;
    }
    // CRITICAL: re-measure when CONTENT mounts/unmounts. The inner is `flex-1`
    // inside a fixed-height (100dvh) outer, so its BORDER box stays the viewport
    // height no matter how tall the Outlet CONTENT grows — only
    // inner.scrollHeight changes, which a ResizeObserver does NOT report. The
    // handwriting trace pad appearing after a correct answer (or a hint scaffold
    // expanding) is a childList mutation in the subtree, so a MutationObserver
    // catches it. measure() is rAF-coalesced, so incidental churn (sparkles,
    // speech bubble) is cheap.
    //
    // MA-622 R1: also watch `style` attribute mutations (attributeFilter
    // keeps this to style only — class/data-* churn from Tailwind states is
    // NOT observed). This is what lets a FROZEN pass's contentOverflowPx stay
    // accurate: the pad-height clamp (padHeightFor) changes a descendant's
    // inline aspectRatio, a STYLE mutation, not childList — without this the
    // observer never saw it, so contentOverflowPx (and the outer's
    // overflow-y:auto fallback) could stay stuck at a stale, pre-clamp
    // reading. No feedback loop: our OWN transform is also a style mutation
    // on this same `inner` node, so setting it fires one extra measure() —
    // but by then freezeKeyRef already matches freezeKey, so that pass takes
    // the FROZEN branch (recomputes contentOverflowPx only, never calls
    // setScale again) and bails out via the `prev === next` state-update
    // guards once the DOM stops changing. A genuine no-op style write (same
    // value) doesn't even trigger the DOM to mark a mutation, and a genuine
    // change reflects a real, intended re-measure — not a runaway loop.
    // MA-648 — also watch `characterData` (a text node's `.data` changing in
    // place — no element added/removed, no attribute touched, so neither
    // childList nor the attribute clause above ever saw it) and add `"class"`
    // to attributeFilter (Tailwind conditional styling ships as a className
    // swap, not inline style). RED review found a live, un-rescued shipping
    // path of exactly this shape: the MA-483/489 reveal pal bubble
    // (`MathSession.tsx` ~L1744-1752, byte-for-byte siblings at
    // `ReadingSession.tsx` ~L1668-1680 and `MixedSession.tsx` ~L1483-1493).
    // On the reveal-after-struggle path the bubble's `<div>` keeps its DOM
    // identity (no `key`) while `palMessage` grows from a short line to a
    // multi-sentence hint — React writes `textNode.data` in place
    // (characterData only) — AND its `className` swaps the small
    // `whitespace-nowrap` pill for the large `max-w-[26rem] … text-3xl`
    // wrapping bubble (`class` only — no inline `style` on this element).
    // Nothing else in the frame rescues it: `showTraceBonus` does NOT flip on
    // this path, so the tiles/hint block stays mounted, and MA-622
    // deliberately replaced ITS OWN removal with an `invisible` **class**
    // toggle (the `:1557-1558`-era comment: "kept mounted … unmounting this
    // line would itself shift the prompt") — so there is no childList
    // mutation anywhere in the subtree to rescue this pass either. Without
    // `characterData`/`class` coverage, `contentOverflowPx` never
    // recomputes, `frozenOverflow` never flips, and `.sb-root` stays
    // `overflow-hidden` with the bubble clipped below the fold — one
    // confirmed cause of the founder's "stuck" laptop report (transient here
    // — `REVEAL_DWELL_MS` later a real rescale runs — but a cause, not
    // necessarily the only one; see the poll below for the class of stuck
    // case this can't cover). The extra firings this invites are cheap for
    // the same reason the style-mutation firings already were: measure() is
    // rAF-coalesced (one call wins the frame) and its frozen branch only
    // recomputes `contentOverflowPx`, guarded by `prev === next` state-update
    // checks — a characterData/class mutation that doesn't change the
    // measured height is a no-op re-render, not a re-layout.
    if (typeof MutationObserver !== "undefined" && inner) {
      const mo = new MutationObserver(() => measure());
      mo.observe(inner, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ["style", "class"],
      });
      moRef.current = mo;
    }
    measure();
  }, [measure]);

  const outerRef = useCallback(
    (node: HTMLDivElement | null) => {
      outerNodeRef.current = node;
      attachObservers();
    },
    [attachObservers],
  );
  const innerRef = useCallback(
    (node: HTMLDivElement | null) => {
      innerNodeRef.current = node;
      attachObservers();
    },
    [attachObservers],
  );

  // Re-measure on breakpoint/active change (the refs may already be attached).
  // Forced: a breakpoint flip is a resize in substance, one of the three
  // triggers the freeze allows.
  useLayoutEffect(() => {
    measure({ forceRescale: true });
    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [measure]);

  // Window-level triggers. The DOM observers are attached via the callback refs
  // above; here we only need the events a ResizeObserver can miss.
  // MA-622 R1: forced — a window resize/orientationchange rescales even when
  // the freezeKey (item) hasn't changed; it's one of the three named triggers.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = () => measure({ forceRescale: true });
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, [measure]);

  // MA-648 — low-frequency safety-net poll, in its OWN effect (deliberately
  // NOT inside attachObservers, which is invoked from callback refs, not an
  // effect — see the comment above attachObservers). A real `useEffect` here
  // gets React's own cleanup contract instead of a hand-rolled one: keyed
  // `[measure, active, isMd]`, so it tears down and re-evaluates on every
  // `active`/`isMd` flip, with no path that can leave a stale-closure
  // interval running. (An earlier version armed this from inside
  // attachObservers/a ref; on a session -> non-session navigation the OLD
  // callback ref only ever gets called with `node=null` (React detaches,
  // never re-attaches, since the non-session branch renders no ref at all),
  // and that stale closure's own `active` was still `true` — so it cleared
  // the old interval and immediately re-armed a new one nothing could ever
  // clear again. A plain effect closing over the CURRENT `active`/`isMd` on
  // each render cannot do that.)
  //
  // The poll's concrete warrant, not an abstract one: it is the only thing
  // in this file that covers a layout change with NO DOM mutation at all —
  // neither observer can ever see one. Two concrete, laptop-realistic
  // examples: a webfont (`font-kid`, the prompt heading) finishing its swap
  // after first paint and rewrapping text at new metrics; a late image
  // decode (`Pal`, `ScaffoldNB`, story art) laying out at its intrinsic size
  // after load. Both change `inner.scrollHeight` with zero childList/
  // attribute/characterData mutation to observe. measure() is cheap enough
  // (MA-964: it now also reads one client rect per descendant of the inner,
  // linear in the session DOM, see measureInnerHeight) to call
  // unconditionally every ~750ms while the fit is active on the
  // tablet/desktop path: it early-returns on no state change, and its frozen
  // branch only ever recomputes `contentOverflowPx` (guarded by `prev ===
  // next`) — it cannot move `scale`/`floorScroll` on its own (see
  // `shouldRescale`: a poll tick supplies neither a new freezeKey nor
  // `forceRescale`), so it cannot reintroduce MA-622's "no rescale inside an
  // item" regression.
  useEffect(() => {
    if (!active || !isMd) return;
    const id = setInterval(() => measure(), 750);
    return () => clearInterval(id);
  }, [measure, active, isMd]);

  // Safety net: disconnect observers when the hook unmounts.
  useEffect(
    () => () => {
      roRef.current?.disconnect();
      roRef.current = null;
      moRef.current?.disconnect();
      moRef.current = null;
    },
    [],
  );

  const innerStyle = fitInnerStyle({ active, isMd, floorScroll, scale });
  // MA-622 R1 — item 3's fallback: the scale itself is frozen (never
  // rescaled to fit the trace pad), so if the pad still doesn't fit inside
  // the 140px floor, the outer switches from overflow-hidden to a scroll
  // container INSTEAD of a rescale. This is orthogonal to floorScroll (the
  // pre-existing "content is fundamentally too tall even at MIN_SCALE"
  // fallback, which also drops the transform) — here the transform/scale is
  // untouched; only the outer becomes scrollable so a caller's
  // scrollIntoView can bring the pad on screen.
  // RED finding 2 — a few px of residual overflow (well under the pal
  // bubble's own bottom margin) is not worth flipping the outer into a
  // scroll container for: on Windows/Linux that introduces a scrollbar,
  // which reflows centered content horizontally too — a visible defect on
  // its own. scrollIntoView still works inside an overflow:hidden container
  // (it sets scrollTop directly), so staying overflow-hidden below this
  // tolerance loses nothing. Only genuine, floor-driven overflow (the 140px
  // pad-height floor not fitting) should flip it.
  const frozenOverflow =
    active && isMd && !floorScroll && freezeKey != null && contentOverflowPx > OVERFLOW_TOLERANCE_PX;
  // MA-823 — a marker class naming the fit mode on the tablet/desktop path,
  // so a capture or e2e can tell the MIN_SCALE scroll fallback (`floorScroll`:
  // no transform, the page scrolls at full size) from a frozen scale whose
  // content overflows (`frozenOverflow`: transform kept, the outer scrolls).
  // At a frozen scale of exactly 1 the two are otherwise indistinguishable in
  // the DOM. No styles hang off these classes.
  const fitModeClass = active && isMd ? (floorScroll ? " sb-fit-fallback" : " sb-fit-scale") : "";
  const outerClassName =
    "sb-root flex h-[100dvh] flex-col " +
    (active && isMd && !floorScroll && !frozenOverflow
      ? "overflow-hidden"
      : "overflow-y-auto overscroll-none") +
    fitModeClass;

  const outerDataAttrs = fitProbeAttrs({ active, isMd, floorScroll, scale });

  return { outerRef, innerRef, outerClassName, innerStyle, setFreezeKey, contentOverflowPx, outerDataAttrs };
}
